import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Modal as AntdModal,
  Radio,
  Spin,
} from "antd";

import {
  adjustLeaveBalance,
  getEmployeeBalanceDetail,
} from "../../../../../../../services/attendanceService";
import { getErrorMessage } from "../../../../../../../core/data/redux/utils/errorUtils";
import { notify } from "../../../../../../../services/toast.config";

import type {
  AppliedTo,
  EmployeeBalanceRow,
  LeaveSummary,
  LeaveTypeSummary,
} from "../types";

const getCurrentMonthKey = (): string => {
  const now = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
  );
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

type AdjustBalanceModalProps = {
  open: boolean;
  row: EmployeeBalanceRow | null;
  onClose: () => void;
  onSuccess: () => void;
};

type Step = "form" | "confirm";

type FormValues = {
  appliedTo: AppliedTo;
  delta: number;
  reason: string;
};

const computeProjection = (
  base: LeaveTypeSummary | null,
  values: FormValues,
  monthKey: string | null,
): LeaveTypeSummary => {
  const cloned: LeaveTypeSummary =
    base && Object.keys(base).length > 0
      ? JSON.parse(JSON.stringify(base))
      : {
        leaveTypeId: 0,
        leaveTypeName: "",
        yearlyTotal: 0,
        annualBalance: 0,
        carryForwardFromPrevYear: 0,
        monthlyData: {},
      };

  if (!cloned.monthlyData) cloned.monthlyData = {};
  const { delta, appliedTo } = values;

  if (monthKey) {
    if (!cloned.monthlyData[monthKey]) {
      cloned.monthlyData[monthKey] = {
        allocated: 0,
        used: 0,
        balance: 0,
        carriedFromPrev: 0,
      };
    }
    const bucket = cloned.monthlyData[monthKey];
    bucket.balance = (bucket.balance || 0) + delta;
    if (appliedTo === "balance_and_used") {
      bucket.used = Math.max((bucket.used || 0) - delta, 0);
    }
  } else {
    cloned.annualBalance = (cloned.annualBalance || 0) + delta;
  }
  return cloned;
};

const formatBucket = (bucket: {
  allocated?: number;
  used?: number;
  balance?: number;
  carriedFromPrev?: number;
}) =>
  `allocated=${bucket.allocated ?? 0}, used=${bucket.used ?? 0}, balance=${bucket.balance ?? 0
  }, carriedFromPrev=${bucket.carriedFromPrev ?? 0}`;

export const AdjustBalanceModal: React.FC<AdjustBalanceModalProps> = ({
  open,
  row,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);

  const [leaveSummary, setLeaveSummary] = useState<LeaveSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const baseTypeSummary = useMemo<LeaveTypeSummary | null>(() => {
    if (!row || !leaveSummary) return null;
    return leaveSummary[String(row.leaveTypeId)] || null;
  }, [row, leaveSummary]);

  const effectiveMonthKey = useMemo(
    () => (row?.isMonthlyAccrual ? getCurrentMonthKey() : null),
    [row?.isMonthlyAccrual],
  );

  useEffect(() => {
    if (!open || !row) return;
    form.resetFields();
    form.setFieldsValue({
      appliedTo: "balance_only",
      delta: 0.5,
      reason: "",
    });
    setStep("form");
    setErrorMsg(null);
    setPendingValues(null);

    let cancelled = false;
    const fetchDetail = async () => {
      setDetailLoading(true);
      setLeaveSummary(null);
      try {
        const res = await getEmployeeBalanceDetail({
          user_id: row.userId,
          year: row.year,
        });
        if (cancelled) return;
        setLeaveSummary(res?.data?.data?.leaveSummary || {});
      } catch (err) {
        if (cancelled) return;
        notify.error(getErrorMessage(err));
        setLeaveSummary({});
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [open, row?.userId, row?.year, form]);

  const handleNext = async () => {
    setErrorMsg(null);
    try {
      const values = await form.validateFields();
      if (
        values.appliedTo === "balance_and_used" &&
        !effectiveMonthKey
      ) {
        form.setFields([
          {
            name: "appliedTo",
            errors: [
              "'Balance + Used' is only valid for monthly leave types (current month).",
            ],
          },
        ]);
        return;
      }
      if (values.delta % 0.5 !== 0 || values.delta === 0) {
        form.setFields([
          {
            name: "delta",
            errors: ["Delta must be a non-zero multiple of 0.5"],
          },
        ]);
        return;
      }
      setPendingValues(values);
      setStep("confirm");
    } catch {
      // antd handles per-field errors automatically
    }
  };

  const handleConfirm = async () => {
    if (!pendingValues || !row) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const payload: Record<string, any> = {
        user_id: row.userId,
        leave_type_id: row.leaveTypeId,
        year: row.year,
        delta: pendingValues.delta,
        applied_to: pendingValues.appliedTo,
        reason: pendingValues.reason,
      };
      if (effectiveMonthKey) payload.month_key = effectiveMonthKey;

      const res = await adjustLeaveBalance(payload);
      const responseMessage = res?.data?.message;
      if (responseMessage && Array.isArray(responseMessage)) {
        throw new Error(responseMessage[0]?.msg || "Adjustment failed");
      }

      notify.success("Balance adjusted successfully");
      onSuccess();
      onClose();
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
      setStep("form");
    } finally {
      setSubmitting(false);
    }
  };

  const projection = useMemo(() => {
    if (!pendingValues) return null;
    return computeProjection(baseTypeSummary, pendingValues, effectiveMonthKey);
  }, [pendingValues, baseTypeSummary, effectiveMonthKey]);

  if (!row) return null;

  const beforeBucket = effectiveMonthKey
    ? baseTypeSummary?.monthlyData?.[effectiveMonthKey] || {
      allocated: 0,
      used: 0,
      balance: 0,
      carriedFromPrev: 0,
    }
    : null;
  const afterBucket = effectiveMonthKey
    ? projection?.monthlyData?.[effectiveMonthKey]
    : null;

  return (
    <AntdModal
      open={open}
      onCancel={onClose}
      centered
      width={620}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
      zIndex={9999}
      getContainer={() => document.body}
      styles={{
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0, 0, 0, 0.45)",
          zIndex: 9998,
        },
        content: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
          borderRadius: 12,
        },
        header: {
          flexShrink: 0,
          padding: "16px 24px 12px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 0,
        },
        body: {
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "20px 24px 8px",
        },
        footer: {
          flexShrink: 0,
          padding: "12px 24px 16px",
          borderTop: "1px solid #f0f0f0",
          marginTop: 0,
        },
      }}
      title={
        <span style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
          {step === "form"
            ? `Adjust Balance — ${row.leaveTypeName}`
            : "Confirm Adjustment"}
        </span>
      }
      footer={
        step === "form" ? (
          <div style={{ textAlign: "right" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary ms-2"
              onClick={handleNext}
              disabled={submitting || detailLoading}
            >
              Review
            </button>
          </div>
        ) : (
          <div style={{ textAlign: "right" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setStep("form")}
              disabled={submitting}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary ms-2"
              onClick={handleConfirm}
              disabled={submitting || detailLoading}
            >
              {submitting ? "Submitting..." : "Confirm Adjustment"}
            </button>
          </div>
        )
      }
    >
      {detailLoading && (
        <div className="text-center py-5">
          <Spin />
          <div className="text-muted small mt-2">Loading current balance…</div>
        </div>
      )}

      {!detailLoading && errorMsg && (
        <Alert
          type="error"
          message={errorMsg}
          showIcon
          className="mb-3"
          closable
          onClose={() => setErrorMsg(null)}
        />
      )}

      {!detailLoading && step === "form" && (
        <Form form={form} layout="vertical">
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <div className="text-muted small">Employee</div>
              <div className="fw-semibold">{row.userName}</div>
            </div>
            <div className="col-md-3">
              <div className="text-muted small">Leave Type</div>
              <div className="fw-semibold">{row.leaveTypeName}</div>
            </div>
            <div className="col-md-3">
              <div className="text-muted small">Year</div>
              <div className="fw-semibold">{row.year}</div>
            </div>
          </div>

          <Alert
            type="info"
            showIcon
            className="mb-3"
            message={
              effectiveMonthKey
                ? `Adjustment will be applied to the current month (${effectiveMonthKey}).`
                : "Year-level adjustment (this leave type is not monthly accrual)."
            }
          />

          <Form.Item
            label="Apply To"
            name="appliedTo"
            rules={[{ required: true, message: "Choose where to apply" }]}
          >
            <Radio.Group>
              <Radio value="balance_only">Balance only</Radio>
              <Radio
                value="balance_and_used"
                disabled={!effectiveMonthKey}
              >
                Balance + Used (record as usage)
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Delta (days, +/-, multiples of 0.5)"
            name="delta"
            rules={[
              { required: true, message: "Delta is required" },
              {
                validator: (_, value: number) => {
                  if (value === undefined || value === null) {
                    return Promise.reject("Delta is required");
                  }
                  if (value === 0) {
                    return Promise.reject("Delta must be non-zero");
                  }
                  if (value % 0.5 !== 0) {
                    return Promise.reject("Delta must be a multiple of 0.5");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber step={0.5} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Reason"
            name="reason"
            rules={[
              { required: true, message: "Reason is required" },
              { min: 3, message: "Reason must be at least 3 characters" },
              { max: 500, message: "Reason cannot exceed 500 characters" },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Explain why this adjustment is being made"
            />
          </Form.Item>
        </Form>
      )}

      {!detailLoading && step === "confirm" && pendingValues && (
        <div>
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <div className="text-muted small">Employee</div>
              <div className="fw-semibold">{row.userName}</div>
            </div>
            <div className="col-md-3">
              <div className="text-muted small">Leave Type</div>
              <div className="fw-semibold">{row.leaveTypeName}</div>
            </div>
            <div className="col-md-3">
              <div className="text-muted small">Mode</div>
              <div className="fw-semibold">{pendingValues.appliedTo}</div>
            </div>
          </div>

          <div className="balance-card before mb-3 p-3">
            <div className="fw-semibold mb-2">Before</div>
            {effectiveMonthKey && beforeBucket ? (
              <div className="text-muted small">
                {effectiveMonthKey} — {formatBucket(beforeBucket)}
              </div>
            ) : (
              <div className="text-muted small">
                annualBalance = {baseTypeSummary?.annualBalance ?? 0}
              </div>
            )}
          </div>

          <div className="balance-card after mb-3 p-3">
            <div className="fw-semibold mb-2">After</div>
            {effectiveMonthKey && afterBucket ? (
              <div className="small">
                {effectiveMonthKey} — {formatBucket(afterBucket)}
              </div>
            ) : (
              <div className="small">
                annualBalance = {projection?.annualBalance ?? 0}
              </div>
            )}
          </div>

          <div className="mb-2">
            <span className="fw-semibold">Delta:</span>{" "}
            {pendingValues.delta > 0
              ? `+${pendingValues.delta}`
              : pendingValues.delta}{" "}
            day(s)
          </div>
          <div>
            <span className="fw-semibold">Reason:</span> {pendingValues.reason}
          </div>
        </div>
      )}
    </AntdModal>
  );
};

export default AdjustBalanceModal;
